import { Dayjs } from 'dayjs';

export type TimelineVariant = 'data' | 'timeline';
export type TimelineGranularity = 'week' | 'month';
export type TimelineAxis = 'calendar' | 'bookings';

export type BookingStatus =
  | 'AVAILABLE'
  | 'PARTIALLY_AVAILABLE'
  | 'BOOKED'
  | 'CONFLICT'
  | 'UNAVAILABLE';

/**
 * The availability answers a row can give that mean "you cannot book this period", as returned by
 * the space and package list endpoints. Deliberately not every non-`AVAILABLE` value: a bookings
 * row carries a booking's lifecycle status, and `DRAFT` or `PENDING` there says nothing about
 * whether the inventory can be sold.
 *
 * `OUTSIDE_AVAILABILITY` is the one value no endpoint sends — the gap split stamps it on the
 * stretch of a picked period that falls outside the row's own availability window, which is why
 * it is absent from the `toUnavailableStatus` whitelist.
 */
export type UnavailableStatus = 'PARTIALLY_AVAILABLE' | 'BOOKED' | 'NA' | 'OUTSIDE_AVAILABILITY';

/**
 * A row's own availability window as the timeline endpoints report it — raw UTC-canon instants or
 * bare `YYYY-MM-DD` days, both accepted by the collapse helpers. Either bound may be missing on
 * defensive callers; an incomplete window disables the gap split rather than guessing.
 */
export interface TimelineAvailabilityWindow {
  start?: string;
  end?: string;
}

export const TIMELINE_GRANULARITY_LS_KEY = 'generatik_timeline_granularity';

export interface TimelineBooking {
  id: string;
  start: string | Date | Dayjs;
  end: string | Date | Dayjs;
  isOwn: boolean;
  status?: BookingStatus;
  /** Renders at reduced opacity — used for out-of-window filler bars. */
  isBackground?: boolean;
  /**
   * The row's own availability status, set on a bar that would otherwise have been drawn green.
   *
   * Green is computed from occupancy, which is approved bookings and nothing else — so a row whose
   * availability window simply does not cover the period comes back unavailable while no segment
   * blocks it. Where the window is known the free stretches split at its edges — green inside,
   * OUTSIDE_AVAILABILITY beyond; where it is not, they carry the row's answer whole: orange fill,
   * status text, status-led tooltip.
   *
   * Its own field rather than a `status` value, because occupancy bars are already emitted as
   * `BOOKED` — colouring off the status would repaint every occupied bar in the app.
   */
  unavailableStatus?: UnavailableStatus;
  /**
   * The next bar on the row starts the day after this one ends. Set by `fuseAdjacentBars`; the
   * renderers and the bookings axis draw such a bar through its end day so the two meet at one
   * boundary instead of leaving a one-day column between them. `end` is untouched — the tooltip
   * still names the day the booking really ends.
   */
  abutsNext?: boolean;
  /**
   * The order this bar belongs to, when the viewer is allowed to open it. Set by the backend, which
   * withholds it from anyone who is neither a party to the order nor the owner of the inventory it
   * books — so its presence is the whole of the permission check, and a bar that has it is a link.
   */
  orderId?: string;
  /**
   * Package rows only: the package is blocked because one of its member spaces is booked, rather
   * than because the package itself was. The bar looks the same either way — the period is blocked
   * regardless — so the tooltip is what tells the two apart.
   */
  partial?: boolean;
  /**
   * Draws as a double-arrow indicator (`<---->`) under the rail instead of a solid bar — used to
   * mark a period being added to a booking, such as a staged extension. Grid variant only; the
   * list-view strip skips these.
   */
  isExtension?: boolean;
  /**
   * Draws as an accent/green striped bar with an "Extension pending" tooltip — an extension
   * request submitted and awaiting the location's decision. Grid variant only; the list-view
   * strip skips these.
   */
  isPendingExtension?: boolean;
  /**
   * A committed redecoration period — the booking re-decorated from this segment's start. Drawn
   * as a distinct solid segment; carries `operationIds` so it can be cancelled from the bar.
   */
  isRedecoration?: boolean;
  /**
   * A committed extension period of the row's own booking. Same accent as the base bar — it IS
   * this campaign — but a separate segment with its own tooltip, so what was bought later reads
   * as bought later.
   */
  isExtensionSegment?: boolean;
  /**
   * An own booking that belongs to ANOTHER order than the rail's. Set at assembly time (only a
   * caller knows which order the rail is about), it moves the bar out of the accent family so
   * the accent stays exclusive to the order being viewed.
   */
  isOwnOtherOrder?: boolean;
  /**
   * A staged, uncommitted redecoration. Drawn as an indicator like `isExtension`, never as a
   * segment — nothing splits the bar until it exists on the server.
   */
  isStagedRedecoration?: boolean;
  /** Every operation id this segment represents, so a cancellation can target all of them. */
  operationIds?: string[];
  /**
   * Whether an `onBarClick` handler should actually act on this specific bar.
   *
   * `onBarClick` is opt-in per table, not per bar — a table that passes it may still draw bars
   * (like the original, un-redecorated segment) it never intends the handler to fire for. This is
   * the per-bar signal `TimelinePeriodCell` uses to decide the pointer cursor and the click
   * handler's attachment, independently of whether `onBarClick` exists at all: absent, both stay
   * exactly as if no bar were ever clickable.
   */
  isClickable?: boolean;
  /**
   * The owning client's custom timeline color, configured by the location on the client record.
   * Consumed ONLY through `isValidTimelineColor` — a malformed value renders as unset. Never set
   * on state bars (free gaps, conflicts, unavailability): it recolors "booked by X" fills only.
   */
  clientColor?: string;
  /**
   * The owning client's display name (`companyName`), rendered as a `Client:` line in the bar
   * tooltip. Location viewers only: assembly either copies it from backend-gated payload fields
   * or stamps it behind a `userType === 'location'` check.
   */
  clientName?: string;
}

export interface TimelineRange {
  start: Dayjs;
  end: Dayjs;
  periods: Dayjs[];
}

export const TIMELINE_PERIOD_FIELD_PREFIX = '__period_';
export const TIMELINE_PERIOD_COLUMN_WIDTH = 120;
